/**
 * Auth Utilities
 *
 * Token management is delegated to @room/frontend-sdk to avoid duplication.
 * This module provides types and redirect utilities used by the SDK's
 * layout wrappers (RootLayout, PageLayout).
 */

export interface SessionUser {
  id: string;
  username: string;
  email: string;
  role: string;
}

export interface APIResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface SessionResponse {
  user: SessionUser;
}

// Re-export from frontend-sdk to avoid duplicate implementations.
// Apps should import token helpers from @room/frontend-sdk directly;
// these re-exports exist for internal SDK use only.
export { getSessionToken, removeSessionToken } from '@room/frontend-sdk';

import { useBundleModeStore } from '../stores/bundleMode';

const LOGIN_REDIRECT_KEY = 'auth_redirect_in_progress';
const LOGIN_REDIRECT_TTL_MS = 8000;

export const getLoginRedirectUrl = (returnTo: string): string =>
  `/login?returnTo=${encodeURIComponent(returnTo)}`;

export const shouldRedirectToLogin = (pathname: string): boolean => {
  if (typeof window === 'undefined') return false;
  if (pathname.startsWith('/login')) return false;

  // CRITICAL: Never redirect in bundle mode (running in overlay container)
  // Bundle apps inherit authentication from parent page (home screen)
  if (window.__ROOM_BUNDLE_MODE__) {
    return false;
  }

  // Defense in depth: also check the zustand store for an active bundle app.
  // Covers the race condition where setActiveBundleApp() was called but the
  // bundle JS hasn't executed mount() yet to set __ROOM_BUNDLE_MODE__.
  if (useBundleModeStore.getState().activeBundleApp) {
    return false;
  }

  const now = Date.now();
  const existing = window.sessionStorage.getItem(LOGIN_REDIRECT_KEY);
  if (existing) {
    const last = Number(existing);
    if (!Number.isNaN(last) && now - last < LOGIN_REDIRECT_TTL_MS) {
      return false;
    }
  }

  window.sessionStorage.setItem(LOGIN_REDIRECT_KEY, String(now));
  return true;
};

export const clearLoginRedirect = (): void => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(LOGIN_REDIRECT_KEY);
};
